/**
 * Calculs purs de la répartition comptable (ARTO-58), PARTAGÉS par l'API et la
 * console : le serveur calcule les mois et les encaissements avec ces
 * fonctions, l'écran en tire l'aperçu d'un encaissement avant de l'envoyer.
 * Un seul code : l'aperçu ne peut pas diverger de ce que le serveur écrira.
 *
 * Sans base, sans horloge, sans dépendance : des entiers en entrée, des
 * entiers en sortie. Montants en centimes, parts et taux en points de base
 * (10 000 = 100 %). RIEN N'EST ÉCRIT ICI : ni tarif, ni pourcentage — les
 * parts sont celles que la direction saisit.
 *
 * LA TVA NE SE PARTAGE PAS : elle est collectée pour l'État. La répartition
 * porte sur le HT.
 */
import { arrondirCentimes } from '../tax/arrondi.js';
import { POINTS_DE_BASE } from './commun.js';
// ── TVA, HT et TTC ───────────────────────────────────────────────────────────
/**
 * La TVA d'UNE ligne saisie hors taxes : son HT par son taux, arrondie au
 * centime. Chaque ligne est arrondie avant d'être additionnée, comme le serait
 * chaque facture.
 */
export function tvaDe(htCentimes, tauxPointsDeBase) {
    return arrondirCentimes((htCentimes * tauxPointsDeBase) / POINTS_DE_BASE);
}
/**
 * Le HT d'un montant reçu TOUTES TAXES COMPRISES : `TTC × 10 000 / (10 000 +
 * taux)`, arrondi au centime le plus proche (une demie vers le haut). Calcul
 * en ENTIERS LONGS (`BigInt`) : `TTC × 10 000` dépasse vite 2^53, au-delà
 * duquel un nombre flottant perd des unités — exact pour tout entier, sans
 * flottant ni demi-centime perdu. La TVA d'une telle ligne est `TTC − HT`, si
 * bien que HT + TVA = TTC exactement — ce que `tvaDe(HT)` ne garantit pas
 * (TTC 0,03 € à 20 % : HT 0,03 €, et 20 % de 0,03 € arrondi ferait 0,04 €).
 */
export function htDepuisTtc(ttcCentimes, tauxPointsDeBase) {
    const numerateur = BigInt(ttcCentimes) * BigInt(POINTS_DE_BASE);
    const denominateur = BigInt(POINTS_DE_BASE + tauxPointsDeBase);
    let quotient = numerateur / denominateur;
    let reste = numerateur % denominateur;
    // La division des entiers longs tronque vers zéro : ramenée à la division
    // entière par défaut, pour un montant négatif.
    if (reste < 0n) {
        quotient -= 1n;
        reste += denominateur;
    }
    return Number(2n * reste >= denominateur ? quotient + 1n : quotient);
}
/** Comment un montant reçu a été saisi : hors taxes, ou toutes taxes comprises. */
export const SAISIES_MONTANT = ['ht', 'ttc'];
/**
 * Les trois montants d'une recette saisie HT ou TTC. Saisie HT : la TVA se
 * calcule sur le HT (`tvaDe`). Saisie TTC : le HT s'en déduit
 * (`htDepuisTtc`), la TVA est le reste. Dans les deux cas, HT + TVA = TTC.
 */
export function montantsRecette(saisie, montantCentimes, tauxPointsDeBase) {
    if (saisie === 'ttc') {
        const htCentimes = htDepuisTtc(montantCentimes, tauxPointsDeBase);
        return { htCentimes, tvaCentimes: montantCentimes - htCentimes, ttcCentimes: montantCentimes };
    }
    const tvaCentimes = tvaDe(montantCentimes, tauxPointsDeBase);
    return { htCentimes: montantCentimes, tvaCentimes, ttcCentimes: montantCentimes + tvaCentimes };
}
// ── Plus fort reste ──────────────────────────────────────────────────────────
/**
 * Répartit un HT entre des parts en points de base, par la méthode du PLUS
 * FORT RESTE : chacun reçoit d'abord le plancher de sa part exacte
 * (`HT × part / 10 000`), puis les centimes restants vont, un par un, aux plus
 * forts restes ; à reste égal, au premier dans l'ordre de la règle.
 *
 * Invariant : les parts somment exactement le HT, et chacune est à moins d'un
 * centime de sa part exacte. Une part nulle ne reçoit jamais rien.
 *
 * Tout est entier : `HT × part` reste exact tant qu'il ne dépasse pas 2^53,
 * soit un HT d'environ 9 milliards d'euros (un encaissement est plafonné à un
 * milliard, `MONTANT_ENCAISSEMENT_MAX_CENTIMES`), et le reste se compare sans
 * flottant. Des parts qui ne somment pas 10 000 n'ont
 * pas pu être écrites (`defautDesParts`) : les recevoir ici est une faute.
 */
export function repartirAuPlusFortReste(htCentimes, parts) {
    const somme = parts.reduce((total, part) => total + part, 0);
    if (somme !== POINTS_DE_BASE) {
        throw new Error(`Parts de répartition invalides : ${String(somme)} points de base.`);
    }
    const produits = parts.map((part) => htCentimes * part);
    const resultat = produits.map((produit) => Math.floor(produit / POINTS_DE_BASE));
    let reste = htCentimes - resultat.reduce((total, montant) => total + montant, 0);
    const ordre = produits
        .map((produit, rang) => ({ rang, reste: produit % POINTS_DE_BASE }))
        .sort((a, b) => b.reste - a.reste || a.rang - b.rang);
    for (const { rang } of ordre) {
        if (reste === 0)
            break;
        resultat[rang] = (resultat[rang] ?? 0) + 1;
        reste -= 1;
    }
    return resultat;
}
/**
 * Le VECTEUR DE PARTS EFFECTIF d'une version pour des recettes que certains
 * bénéficiaires excluent : la part de chacun d'eux est transférée au
 * bénéficiaire du reste, qui s'ajoute à la fin s'il n'est pas dans la version.
 * L'ordre de la version est gardé : il départage le plus fort reste. La somme
 * reste de 10 000.
 *
 * `null` quand une part non nulle serait à transférer sans bénéficiaire du
 * reste : rien n'est alors inventé. Les exclusions du bénéficiaire du reste
 * lui-même sont sans effet (la route les refuse).
 */
export function vecteurEffectif(parts, exclus, resteId) {
    let transfert = 0;
    const vecteur = parts.map((part) => {
        const beneficiaireId = String(part.beneficiaireId);
        if (beneficiaireId !== resteId && exclus.has(beneficiaireId) && part.partPointsDeBase > 0) {
            transfert += part.partPointsDeBase;
            return { beneficiaireId, partPointsDeBase: 0 };
        }
        return { beneficiaireId, partPointsDeBase: part.partPointsDeBase };
    });
    if (transfert === 0)
        return vecteur;
    if (resteId === null)
        return null;
    const reste = vecteur.find((part) => part.beneficiaireId === resteId);
    if (reste)
        reste.partPointsDeBase += transfert;
    else
        vecteur.push({ beneficiaireId: resteId, partPointsDeBase: transfert });
    return vecteur;
}
/**
 * Répartit les fractions d'un type selon les parts d'une version : les
 * fractions sont REGROUPÉES par vecteur de parts effectif (l'ensemble des
 * bénéficiaires qui excluent leur galerie), chaque groupe est réparti au plus
 * fort reste, puis les montants s'additionnent. Sans exclusion, un seul
 * groupe : le HT entier.
 *
 * `exclusDe` dit qui exclut une galerie, dans l'ordre du partage. Invariant :
 * quand `sansReste` est faux et que des parts sont données, les montants
 * somment exactement le HT des fractions, au centime.
 */
export function repartirService(fractions, parts, exclusDe, resteId) {
    const groupes = new Map();
    const exclusions = new Map();
    for (const fraction of fractions) {
        const galerieId = fraction.galerieId;
        const exclus = galerieId === null ? [] : exclusDe(galerieId);
        const cle = exclus.join(',');
        const groupe = groupes.get(cle) ?? { exclus: new Set(exclus), htCentimes: 0 };
        groupe.htCentimes += fraction.htCentimes;
        groupes.set(cle, groupe);
        if (galerieId === null)
            continue;
        for (const beneficiaireId of exclus) {
            const cleExclusion = `${beneficiaireId}|${galerieId}`;
            const ligne = exclusions.get(cleExclusion) ?? { beneficiaireId, galerieId, htCentimes: 0 };
            ligne.htCentimes += fraction.htCentimes;
            exclusions.set(cleExclusion, ligne);
        }
    }
    const montants = new Map();
    let sansReste = false;
    if (parts.length > 0) {
        for (const groupe of groupes.values()) {
            const vecteur = vecteurEffectif(parts, groupe.exclus, resteId);
            if (vecteur === null) {
                // Un groupe sans recette n'a rien de réel à transférer.
                if (groupe.htCentimes > 0)
                    sansReste = true;
                continue;
            }
            const repartis = repartirAuPlusFortReste(groupe.htCentimes, vecteur.map((part) => part.partPointsDeBase));
            vecteur.forEach((part, rang) => {
                montants.set(part.beneficiaireId, (montants.get(part.beneficiaireId) ?? 0) + (repartis[rang] ?? 0));
            });
        }
    }
    return { montants, exclusions: [...exclusions.values()], sansReste };
}
// ── La version applicable, et la répartition d'un type ───────────────────────
/**
 * La version de la grille qui s'applique à ce type pour ce mois : celle de
 * plus grand `depuisMois` qui ne le dépasse pas. `null` avant la première :
 * le type n'est alors pas réparti, rien n'est inventé.
 */
export function regleApplicable(regles, service, mois) {
    let retenue = null;
    for (const regle of regles) {
        if (regle.service !== service || regle.depuisMois > mois)
            continue;
        if (retenue === null || regle.depuisMois > retenue.depuisMois)
            retenue = regle;
    }
    return retenue;
}
/** Le libellé d'un bénéficiaire qu'une version cite et que le partage n'a plus. */
export const BENEFICIAIRE_INCONNU = 'Bénéficiaire inconnu';
/**
 * Répartit les fractions d'un type pour un mois : la version applicable
 * (`regleApplicable`), les exclusions de galeries en vigueur ce mois-là, le
 * bénéficiaire du reste (le premier marqué), puis `repartirService`. C'est la
 * fonction du calcul d'un mois (API) ET de l'aperçu d'un encaissement
 * (console) : un encaissement seul dans son type, ce mois-là, y reçoit
 * exactement la répartition que l'aperçu montre.
 */
export function repartirType(service, fractions, contexte) {
    const { mois, beneficiaires } = contexte;
    const rangs = new Map(beneficiaires.map((b, rang) => [b.id, rang]));
    const rangDe = (id) => rangs.get(id) ?? beneficiaires.length;
    const libelles = new Map(beneficiaires.map((b) => [b.id, b.libelle]));
    const libelleDe = (id) => libelles.get(id) ?? BENEFICIAIRE_INCONNU;
    const reste = beneficiaires.find((b) => b.recoitLeReste === true);
    const resteId = reste === undefined ? null : reste.id;
    // Qui exclut cette galerie ce mois-ci, dans l'ordre du partage. Le
    // bénéficiaire du reste n'exclut rien.
    const exclusDe = (galerieId) => beneficiaires
        .filter((b) => b.id !== resteId &&
        (b.galeriesExclues ?? []).some((exclusion) => exclusion.galerieId === galerieId && exclusion.depuisMois <= mois))
        .map((b) => b.id);
    const regle = regleApplicable(contexte.regles, service, mois);
    const repartition = repartirService(fractions, regle?.parts ?? [], exclusDe, resteId);
    const exclusions = repartition.exclusions.sort((a, b) => rangDe(a.beneficiaireId) - rangDe(b.beneficiaireId) || a.galerieId.localeCompare(b.galerieId));
    const reparti = regle !== null && !repartition.sansReste;
    const parts = [];
    if (reparti) {
        for (const part of regle.parts) {
            parts.push({
                beneficiaireId: part.beneficiaireId,
                libelle: libelleDe(part.beneficiaireId),
                partPointsDeBase: part.partPointsDeBase,
                montantCentimes: repartition.montants.get(part.beneficiaireId) ?? 0,
            });
        }
        const transfere = resteId === null ? 0 : (repartition.montants.get(resteId) ?? 0);
        if (resteId !== null && transfere > 0 && !parts.some((p) => p.beneficiaireId === resteId)) {
            parts.push({
                beneficiaireId: resteId,
                libelle: libelleDe(resteId),
                partPointsDeBase: 0,
                montantCentimes: transfere,
            });
        }
    }
    return {
        regle: regle === null ? null : { id: regle.id, depuisMois: regle.depuisMois },
        parts,
        exclusions,
        sansReste: repartition.sansReste,
        reparti,
    };
}
/**
 * La répartition d'une seule recette — un encaissement — telle que le serveur
 * la sert dans la liste et que l'écran la montre avant l'envoi : son HT, sa
 * galerie, au mois de sa date.
 */
export function repartirRecette(recette, contexte) {
    const resultat = repartirType(recette.service, [{ galerieId: recette.galerieId, htCentimes: recette.htCentimes }], contexte);
    return {
        regle: resultat.regle,
        parts: resultat.parts,
        sansReste: resultat.sansReste,
        reparti: resultat.reparti,
    };
}
//# sourceMappingURL=comptabilite-calcul.js.map